import { adminClient } from "./admin";

export const trackerListJobs = async (params = {}) =>
  (await adminClient().get(`/admin/jobs`, { params })).data;
export const trackerGetMeta = async () =>
  (await adminClient().get(`/admin/jobs/meta`)).data;
export const trackerCreateJob = async (payload) =>
  (await adminClient().post(`/admin/jobs`, payload)).data;
export const trackerGetJob = async (id) =>
  (await adminClient().get(`/admin/jobs/${id}`)).data;
export const trackerUpdateJob = async (id, patch) =>
  (await adminClient().patch(`/admin/jobs/${id}`, patch)).data;
export const trackerDeleteJob = async (id) =>
  (await adminClient().delete(`/admin/jobs/${id}`)).data;
export const trackerMergeJobs = async (keeper_id, source_ids) =>
  (await adminClient().post(`/admin/jobs/merge`, { keeper_id, source_ids })).data;
export const trackerArchiveStats = async () =>
  (await adminClient().get(`/admin/jobs/archive-stats`)).data;
export const trackerAddNote = async (id, formData) =>
  (await adminClient().post(`/admin/jobs/${id}/notes`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })).data;
export const trackerNotifyCustomer = async (id, payload = {}) =>
  (await adminClient().post(`/admin/jobs/${id}/notify-customer`, payload)).data;
export const trackerListTeam = async () =>
  (await adminClient().get(`/admin/team-members`)).data;
export const trackerSaveTeamMember = async (id, payload) =>
  id
    ? (await adminClient().patch(`/admin/team-members/${id}`, payload)).data
    : (await adminClient().post(`/admin/team-members`, payload)).data;
export const trackerDeleteTeamMember = async (id) =>
  (await adminClient().delete(`/admin/team-members/${id}`)).data;
export const trackerReportPipeline = async () =>
  (await adminClient().get(`/admin/jobs/reports/pipeline`)).data;
export const trackerReportRevenue = async (months = 6) =>
  (await adminClient().get(`/admin/jobs/reports/revenue`, { params: { months } })).data;
export const trackerReportAging = async (threshold_days = 3) =>
  (await adminClient().get(`/admin/jobs/reports/aging`, { params: { threshold_days } })).data;
export const trackerExportCsvUrl = (filters = {}) => {
  const u = new URL(adminClient().defaults.baseURL + "/admin/jobs/export.csv");
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") u.searchParams.set(k, String(v));
  });
  return u.toString();
};
export const trackerDownloadCsv = async (filters = {}) => {
  const res = await adminClient().get(`/admin/jobs/export.csv`, {
    params: filters,
    responseType: "blob",
  });
  return res.data;
};

// Status / priority colors used by both Tabular & Kanban views
export const STATUS_COLORS = {
  "New Lead":                 { bg: "#FEF3C7", fg: "#92400E", border: "#F59E0B" },
  "Review":                   { bg: "#DBEAFE", fg: "#1E40AF", border: "#3B82F6" },
  "Waiting for Approval":     { bg: "#FCE7F3", fg: "#9D174D", border: "#EC4899" },
  "Art Work Prep":            { bg: "#E0E7FF", fg: "#3730A3", border: "#6366F1" },
  "Ready for Production":     { bg: "#FAE8FF", fg: "#86198F", border: "#A855F7" },
  "In Production":            { bg: "#FED7AA", fg: "#9A3412", border: "#F97316" },
  "Picked":                   { bg: "#D1FAE5", fg: "#065F46", border: "#10B981" },
  "Ready for Pickup":         { bg: "#CFFAFE", fg: "#155E75", border: "#06B6D4" },
  "Shipped":                  { bg: "#DCFCE7", fg: "#166534", border: "#16A34A" },
  "LOST":                     { bg: "#FEE2E2", fg: "#991B1B", border: "#DC2626" },
  "Waiting for Supplier":     { bg: "#FEF9C3", fg: "#713F12", border: "#CA8A04" },
};

export const JOB_STATUS_LIST = [
  "New Lead", "Review", "Waiting for Approval", "Art Work Prep",
  "Ready for Production", "In Production", "Picked",
  "Ready for Pickup", "Shipped", "LOST", "Waiting for Supplier",
];

export const JOB_QUEUE_LIST = [
  "Digital", "WideFormat", "FlatBed",
  "Finishing - Internal", "Finishing - External", "Production - Offsite",
  "PRODUCTION - WAREHOUSE",
];

// Statuses that mean the job is no longer "in flight" — used both for the
// LATE badge filter below and client-side late detection.
export const JOB_LATE_EXCLUDED_STATUSES = new Set([
  "Shipped", "Picked", "LOST", "Ready for Pickup",
]);

/** Client-side mirror of the backend LATE rule (so the badge appears on
 *  every row immediately, without a round-trip). */
export const isJobLate = (job) => {
  if (!job || !job.due_at) return false;
  if (JOB_LATE_EXCLUDED_STATUSES.has(job.status)) return false;
  if (job.archived) return false;
  const due = new Date(job.due_at).getTime();
  if (Number.isNaN(due)) return false;
  return due < Date.now();
};

export const PRIORITY_COLORS = {
  Critical: { bg: "#DC2626", fg: "#FFFFFF" },
  Rush:     { bg: "#F97316", fg: "#FFFFFF" },
  High:     { bg: "#F59E0B", fg: "#1C1917" },
  Normal:   { bg: "#E7E5E4", fg: "#1C1917" },
};

// Color-coded production queues — used in main table cell + Kanban headers + Job Ticket
export const QUEUE_COLORS = {
  "Digital":               { bg: "#DBEAFE", fg: "#1E3A8A", border: "#3B82F6" },
  "WideFormat":            { bg: "#FEF3C7", fg: "#78350F", border: "#F59E0B" },
  "FlatBed":               { bg: "#FCE7F3", fg: "#831843", border: "#EC4899" },
  "Finishing - Internal":  { bg: "#E0E7FF", fg: "#312E81", border: "#6366F1" },
  "Finishing - External":  { bg: "#F3E8FF", fg: "#581C87", border: "#A855F7" },
  "Production - Offsite":  { bg: "#D1FAE5", fg: "#064E3B", border: "#10B981" },
  "PRODUCTION - WAREHOUSE":{ bg: "#F5F5F4", fg: "#1C1917", border: "#78716C" },
};

// Customer-type swatches — used in Job Ticket print header
export const CUSTOMER_TYPE_COLORS = {
  "UPS Store":              { bg: "#FFB500", fg: "#1C1917" },
  "House Account":          { bg: "#1E40AF", fg: "#FFFFFF" },
  "RETAIL":                 { bg: "#F97316", fg: "#FFFFFF" },
  "Non Profit":             { bg: "#15803D", fg: "#FFFFFF" },
  "PrintTales":             { bg: "#7C3AED", fg: "#FFFFFF" },
  "High Value":             { bg: "#DC2626", fg: "#FFFFFF" },
  "Schools":                { bg: "#0E7490", fg: "#FFFFFF" },
};

// Product-tag swatches — surfaced on Job Ticket / drawer / table cells.
// Coarse "what's being printed?" classification.
export const PRODUCT_TAG_COLORS = {
  "Business Cards":          { bg: "#FFB500", fg: "#1C1917" },
  "Flyers":                  { bg: "#F97316", fg: "#FFFFFF" },
  "Brochures":               { bg: "#FB923C", fg: "#FFFFFF" },
  "Postcards":               { bg: "#FBBF24", fg: "#78350F" },
  "Digital Prints":          { bg: "#3B82F6", fg: "#FFFFFF" },
  "Banners":                 { bg: "#EC4899", fg: "#FFFFFF" },
  "Yard Signs":              { bg: "#10B981", fg: "#FFFFFF" },
  "Big Heads":               { bg: "#D946EF", fg: "#FFFFFF" },
  "Posters":                 { bg: "#A855F7", fg: "#FFFFFF" },
  "Acrylics":                { bg: "#8B5CF6", fg: "#FFFFFF" },
  "DTF Prints":              { bg: "#06B6D4", fg: "#FFFFFF" },
  "Stickers / Decals":       { bg: "#F472B6", fg: "#FFFFFF" },
  "Lamination":              { bg: "#6366F1", fg: "#FFFFFF" },
  "Binding":                 { bg: "#4F46E5", fg: "#FFFFFF" },
  "Envelopes":               { bg: "#737373", fg: "#FFFFFF" },
  "Foam Boards / Coroplast": { bg: "#0E7490", fg: "#FFFFFF" },
  "Other":                   { bg: "#A8A29E", fg: "#1C1917" },
};
